import React from 'react';

// Shared parchment-light-green theme (matches ProfileModals + LeaderboardModal).
const OVERLAY = {
  position: 'fixed', inset: 0, backgroundColor: 'rgba(20, 40, 20, 0.55)',
  zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 16,
};
const CARD = {
  width: 460, maxWidth: '96vw',
  backgroundColor: '#e6f0d6',
  border: '2px solid #2d6b1e',
  boxShadow: '0 8px 28px rgba(0,0,0,0.45)',
  padding: '18px 22px 18px',
  fontFamily: 'Arial, Helvetica, sans-serif',
  color: '#1c3f1c',
};
const BTN = (variant) => ({
  backgroundColor: variant === 'primary' ? '#1c3a1c' : (variant === 'danger' ? '#5d2a2a' : '#5b7c47'),
  color: '#ecfcd9',
  border: `1px solid ${variant === 'danger' ? '#3a1a1a' : '#2d6b1e'}`,
  padding: '6px 20px', fontWeight: 'bold', fontSize: 12,
  cursor: 'pointer', margin: '0 4px',
});

/**
 * FriendRequestSendModal
 *
 * Confirmation popup shown when the current user clicks the "🤝 Friend" button
 * on a character profile.  Shows the outcome inline:
 *   - Monster → auto-accepted
 *   - NPC with ≥ 10 affection hearts → accepted
 *   - NPC with < 10 hearts → declined (with hint on affection required)
 *   - Player → pending until the target responds
 */
export const FriendRequestSendModal = ({
  open, onClose, target, apiBase, userId, myUsername, onFriendAdded,
}) => {
  const [phase, setPhase] = React.useState('confirm'); // 'confirm' | 'sending' | 'result'
  const [result, setResult] = React.useState(null);
  const [err, setErr] = React.useState('');

  React.useEffect(() => {
    if (!open) { setPhase('confirm'); setResult(null); setErr(''); }
  }, [open]);

  if (!open || !target) return null;

  const send = async () => {
    setPhase('sending'); setErr('');
    try {
      const r = await fetch(`${apiBase}/friends/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          requester_name: myUsername,
          target_name: target.name,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Request failed.');
      setResult(d);
      setPhase('result');
      if (d.status === 'accepted' && onFriendAdded) onFriendAdded(target.name);
    } catch (e) {
      setErr(String(e.message || e));
      setPhase('confirm');
    }
  };

  return (
    <div style={OVERLAY} onClick={onClose} data-testid="friend-request-overlay">
      <div style={CARD} onClick={(e) => e.stopPropagation()} data-testid="friend-request-modal">
        <div style={{ fontWeight: 'bold', fontSize: 16, textAlign: 'center', marginBottom: 10 }}>
          🤝 Friend Request
        </div>

        {phase === 'confirm' && (
          <>
            <div style={{ fontSize: 13, textAlign: 'center', lineHeight: 1.55 }}>
              Send a friend request to{' '}
              <b style={{ color: target.color || '#1c3f1c' }}>{target.name}</b>?
              {target.isMonster && (
                <div style={{ marginTop: 6, fontStyle: 'italic', color: '#3a5a3a', fontSize: 11 }}>
                  Monsters always accept.
                </div>
              )}
              {target.isNPC && !target.isMonster && (
                <div style={{ marginTop: 6, fontStyle: 'italic', color: '#3a5a3a', fontSize: 11 }}>
                  NPCs require 10 affection ♥ before they&apos;ll accept.
                </div>
              )}
            </div>
            {err && (
              <div style={{ marginTop: 10, textAlign: 'center', color: '#7a1a1a', fontSize: 12 }}>
                {err}
              </div>
            )}
            <div style={{ textAlign: 'center', marginTop: 14 }}>
              <button data-testid="friend-request-send-btn" style={BTN('primary')} onClick={send}>Send</button>
              <button data-testid="friend-request-cancel-btn" style={BTN('secondary')} onClick={onClose}>Cancel</button>
            </div>
          </>
        )}

        {phase === 'sending' && (
          <div style={{ padding: 18, textAlign: 'center', fontStyle: 'italic' }}>Sending…</div>
        )}

        {phase === 'result' && result && (
          <>
            {result.status === 'accepted' && (
              <div style={{ textAlign: 'center', color: '#1a5a10', fontSize: 13 }}>
                ✓ <b>{target.name}</b> accepted your friend request!
                {result.reason === 'monster_auto' && (
                  <div style={{ fontSize: 11, marginTop: 4, fontStyle: 'italic' }}>
                    Beasts recognise their own — bond forged.
                  </div>
                )}
                {result.reason === 'npc_affection' && (
                  <div style={{ fontSize: 11, marginTop: 4, fontStyle: 'italic' }}>
                    Affection ♥ {result.affection} — they consider you a true companion.
                  </div>
                )}
              </div>
            )}
            {result.status === 'declined' && (
              <div style={{ textAlign: 'center', color: '#7a1a1a', fontSize: 13 }}>
                ✗ <b>{target.name}</b> declined.
                <div style={{ fontSize: 11, marginTop: 4, fontStyle: 'italic', color: '#5a3a1a' }}>
                  Affection ♥ {result.affection} of {result.needed} — spend more time with them
                  in-character to raise their affection, then try again.
                </div>
              </div>
            )}
            {result.status === 'pending' && (
              <div style={{ textAlign: 'center', color: '#3a5a3a', fontSize: 13 }}>
                ⏳ Request sent to <b>{target.name}</b>. Awaiting their response.
              </div>
            )}
            <div style={{ textAlign: 'center', marginTop: 14 }}>
              <button data-testid="friend-request-close-btn" style={BTN('primary')} onClick={onClose}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};


/**
 * FriendRequestInboxModal
 *
 * Popup listing incoming friend requests for the current player, with
 * per-row Accept / Decline buttons.  Refetches on open.
 */
export const FriendRequestInboxModal = ({
  open, onClose, apiBase, myUsername, onAccepted,
}) => {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState('');

  const load = React.useCallback(async () => {
    if (!apiBase || !myUsername) return;
    setLoading(true); setErr('');
    try {
      const r = await fetch(`${apiBase}/friends/requests?username=${encodeURIComponent(myUsername)}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed to load.');
      setRows(d.requests || []);
    } catch (e) {
      setErr(String(e.message || e));
    } finally { setLoading(false); }
  }, [apiBase, myUsername]);

  React.useEffect(() => { if (open) load(); }, [open, load]);

  const decide = async (req, action) => {
    try {
      const r = await fetch(`${apiBase}/friends/requests/decide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_name: myUsername,
          requester_name: req.requester_name,
          action,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed.');
      if (action === 'accept' && onAccepted) onAccepted(req.requester_name);
      await load();
    } catch (e) {
      setErr(String(e.message || e));
    }
  };

  if (!open) return null;
  return (
    <div style={OVERLAY} onClick={onClose} data-testid="friend-inbox-overlay">
      <div style={CARD} onClick={(e) => e.stopPropagation()} data-testid="friend-inbox-modal">
        <div style={{ fontWeight: 'bold', fontSize: 16, textAlign: 'center', marginBottom: 10 }}>
          🤝 Friend Requests
        </div>
        {loading && <div style={{ textAlign: 'center', padding: 12 }}>Loading…</div>}
        {!loading && err && <div style={{ textAlign: 'center', color: '#7a1a1a' }}>{err}</div>}
        {!loading && !err && rows.length === 0 && (
          <div style={{ textAlign: 'center', padding: 16, fontStyle: 'italic', color: '#3a5a3a' }}>
            No pending friend requests.
          </div>
        )}
        {!loading && rows.length > 0 && (
          <div style={{ background: '#f5faec', border: '1px solid #5b8c47', maxHeight: 320, overflowY: 'auto' }}>
            {rows.map((req) => (
              <div
                key={req.id}
                data-testid={`friend-inbox-row-${req.requester_name}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 10px', borderBottom: '1px dotted #b6cf9c',
                  fontSize: 12,
                }}
              >
                <b style={{ flex: 1, color: '#1c3f1c' }}>{req.requester_name}</b>
                <button
                  data-testid={`friend-inbox-accept-${req.requester_name}`}
                  onClick={() => decide(req, 'accept')}
                  style={{ ...BTN('primary'), padding: '3px 12px', fontSize: 11 }}
                >Accept</button>
                <button
                  data-testid={`friend-inbox-decline-${req.requester_name}`}
                  onClick={() => decide(req, 'decline')}
                  style={{ ...BTN('danger'), padding: '3px 12px', fontSize: 11 }}
                >Decline</button>
              </div>
            ))}
          </div>
        )}
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <button data-testid="friend-inbox-close-btn" style={BTN('secondary')} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};


/**
 * AddFriendSearchModal
 *
 * Popup that lets the current user find a character profile by name,
 * preview the result, and click "Add Friend" (which routes through the
 * same /friends/request flow — auto-accept for monsters, affection-gated
 * for NPCs, pending for players).
 */
export const AddFriendSearchModal = ({
  open, onClose, apiBase, userId, myUsername, onFriendAdded,
}) => {
  const [q, setQ] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [outcome, setOutcome] = React.useState(null); // {status, reason, ...}
  const [err, setErr] = React.useState('');

  React.useEffect(() => {
    if (!open) { setQ(''); setResult(null); setOutcome(null); setErr(''); }
  }, [open]);

  const search = async (name) => {
    setErr(''); setResult(null); setOutcome(null);
    const uname = (name || q).trim();
    if (!uname) return;
    setBusy(true);
    try {
      const r = await fetch(`${apiBase}/whois?username=${encodeURIComponent(uname)}`);
      const d = await r.json();
      if (!r.ok || !d.found) { setErr(`No character found named "${uname}".`); return; }
      setResult(d);
    } catch (e) {
      setErr(String(e.message || e));
    } finally { setBusy(false); }
  };

  const addFriend = async () => {
    if (!result) return;
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${apiBase}/friends/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          requester_name: myUsername,
          target_name: result.username,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed.');
      setOutcome(d);
      if (d.status === 'accepted' && onFriendAdded) onFriendAdded(result.username);
    } catch (e) {
      setErr(String(e.message || e));
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div style={OVERLAY} onClick={onClose} data-testid="add-friend-overlay">
      <div style={CARD} onClick={(e) => e.stopPropagation()} data-testid="add-friend-modal">
        <div style={{ fontWeight: 'bold', fontSize: 16, textAlign: 'center', marginBottom: 10 }}>
          🤝 Add a Friend
        </div>
        <div style={{ fontSize: 12, color: '#3a5a3a', marginBottom: 6 }}>
          Find a user, NPC or monster by name.
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            data-testid="add-friend-search-input"
            className="chat-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
            placeholder="e.g. Aeloria_Thorne or Werewolf"
            style={{
              flex: 1,
              /* Msg #778 — matches the main chat's Say input (green Mandala theme) */
              background: '#f8fef0', color: '#1c3f1c',
              border: '1px solid #5b8c47',
              fontSize: 13,
            }}
          />
          <button data-testid="add-friend-search-btn" style={BTN('primary')} onClick={() => search()} disabled={busy}>
            Search
          </button>
        </div>

        {err && (
          <div style={{ marginTop: 10, textAlign: 'center', color: '#7a1a1a', fontSize: 12 }}>{err}</div>
        )}

        {result && !outcome && (
          <div
            data-testid="add-friend-result"
            style={{
              marginTop: 12,
              background: '#f5faec',
              border: '1px solid #5b8c47',
              padding: '10px 12px',
            }}
          >
            <div style={{ fontWeight: 'bold', color: result.color || '#1c3f1c', fontSize: 14 }}>
              {result.username}
              <span style={{ marginLeft: 8, fontSize: 11, color: '#3a5a3a', fontWeight: 'normal' }}>
                ({result.kind === 'npc' ? 'NPC' : 'user'})
              </span>
            </div>
            {result.picture && (
              <img
                src={result.picture}
                alt={result.username}
                style={{ marginTop: 6, maxWidth: 100, maxHeight: 100, border: '1px solid #2d6b1e' }}
                onError={(e) => (e.target.style.display = 'none')}
              />
            )}
            {result.description && (
              <div
                style={{ marginTop: 6, fontSize: 11, color: '#1c3f1c', maxHeight: 120, overflow: 'hidden' }}
                dangerouslySetInnerHTML={{ __html: (result.description || '').split('\n')[0] }}
              />
            )}
            <div style={{ textAlign: 'center', marginTop: 10 }}>
              <button data-testid="add-friend-add-btn" style={BTN('primary')} onClick={addFriend} disabled={busy}>
                Add Friend
              </button>
            </div>
          </div>
        )}

        {outcome && (
          <div
            data-testid="add-friend-outcome"
            style={{
              marginTop: 12, background: '#f5faec', border: '1px solid #5b8c47',
              padding: '10px 12px', textAlign: 'center', fontSize: 13,
            }}
          >
            {outcome.status === 'accepted' && (
              <span style={{ color: '#1a5a10' }}>
                ✓ Added <b>{result.username}</b> to your friends list.
              </span>
            )}
            {outcome.status === 'declined' && (
              <span style={{ color: '#7a1a1a' }}>
                ✗ <b>{result.username}</b> declined (affection ♥ {outcome.affection} of {outcome.needed}).
              </span>
            )}
            {outcome.status === 'pending' && (
              <span style={{ color: '#3a5a3a' }}>
                ⏳ Request sent to <b>{result.username}</b>. Awaiting their reply.
              </span>
            )}
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: 14 }}>
          <button data-testid="add-friend-close-btn" style={BTN('secondary')} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default { FriendRequestSendModal, FriendRequestInboxModal, AddFriendSearchModal };
